import { PUBLISH_RECORDS_STATUS_QUERY } from '@src/gql/publish-record-gql';
import { useDatoQuerySubscription } from '@src/hooks/useDatoQuerySubscription';
import { useEffect } from 'react';

import type { PublishRecordUpdate } from '@src/types.ts/publishRecord';

/**
 * Real-time subscription over an explicit set of `PublishRecord` ids (the
 * in-flight watch set). Successor to `VideosQuerySubscriptionComponent` for
 * render tracking — keyed on records, not videos, so it sees per-variant
 * progress. Watches the id set (not a status filter) so a record's final
 * `succeeded`/`outputJson` push is delivered before its id is pruned.
 *
 * The status query selects only pipeline-mutable fields (+ id), and CDA returns
 * its JSON fields pre-parsed, so each row is already a `PublishRecordUpdate` —
 * no mapping needed. The store merges these onto the full cached record.
 */
const PublishRecordQuerySubscriptionComponent = (props: {
  recordIds: string[];
  onDataReceived: (updates: PublishRecordUpdate[]) => void;
  onError: (error: unknown) => void;
}) => {
  const { recordIds, onDataReceived, onError } = props;
  const { error, data } = useDatoQuerySubscription<
    { allPublishRecords: PublishRecordUpdate[] },
    { ids: string[] }
  >({
    query: PUBLISH_RECORDS_STATUS_QUERY,
    variables: { ids: recordIds },
  });

  useEffect(() => {
    if (error) {
      onError(error);
    } else if (data?.allPublishRecords?.length) {
      onDataReceived(data.allPublishRecords);
    }
  }, [data, error]);

  return <></>;
};

export default PublishRecordQuerySubscriptionComponent;
